import React, { useEffect, useState } from "react";
import api from "../api";
import {
  BookOpen, Plus, Pencil, Trash2, RotateCcw, FlaskConical,
  Save, X, Check, AlertTriangle, Star,
} from "lucide-react";

const EMPTY = {
  category: "",
  keywords: "",
  explanation: "",
  order: 500,
  is_default: false,
};

export default function SpiegazioniCriticita() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | "new" | {id, ...}
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [okMsg, setOkMsg] = useState("");
  const [testText, setTestText] = useState("");
  const [testResult, setTestResult] = useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/explanations");
      setRules(data || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { reload(); }, []);

  const startNew = () => {
    setEditing("new");
    setForm({ ...EMPTY, order: (rules.length + 1) * 10 });
    setErr("");
  };

  const startEdit = (r) => {
    setEditing(r);
    setForm({
      category: r.category,
      keywords: (r.keywords || []).join(", "),
      explanation: r.explanation,
      order: r.order || 500,
      is_default: !!r.is_default,
    });
    setErr("");
  };

  const cancelEdit = () => { setEditing(null); setErr(""); };

  const save = async () => {
    setErr(""); setOkMsg("");
    if (!form.category.trim() || form.explanation.trim().length < 10) {
      setErr("Compila categoria e spiegazione (min. 10 caratteri).");
      return;
    }
    const payload = {
      category: form.category.trim(),
      keywords: form.keywords.split(",").map(s => s.trim()).filter(Boolean),
      explanation: form.explanation.trim(),
      order: parseInt(form.order, 10) || 500,
      is_default: !!form.is_default,
    };
    setSaving(true);
    try {
      if (editing === "new") {
        await api.post("/explanations", payload);
        setOkMsg("Regola creata.");
      } else {
        await api.put(`/explanations/${editing.id}`, payload);
        setOkMsg("Regola aggiornata.");
      }
      setEditing(null);
      await reload();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Errore durante il salvataggio.");
    } finally { setSaving(false); }
  };

  const remove = async (r) => {
    if (r.is_default) {
      alert("Non puoi eliminare la regola di default. Imposta prima un'altra come default.");
      return;
    }
    if (!window.confirm(`Eliminare la regola "${r.category}"?`)) return;
    await api.delete(`/explanations/${r.id}`);
    await reload();
  };

  const resetDefaults = async () => {
    if (!window.confirm(
      "Ripristinare TUTTE le regole ai valori di fabbrica? " +
      "Le tue modifiche andranno perse."
    )) return;
    await api.post("/explanations/reset-defaults");
    setOkMsg("Regole ripristinate.");
    await reload();
  };

  const runTest = async () => {
    if (!testText.trim()) return;
    const { data } = await api.post("/explanations/test", { text: testText });
    setTestResult(data);
  };

  return (
    <div data-testid="explanations-page">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <BookOpen size={22} /> Spiegazioni delle criticità
          </h1>
          <p style={{color:"var(--mv-text-muted)", fontSize:13, marginTop:4}}>
            Dizionario delle spiegazioni che compaiono nel PDF di analisi sotto ogni
            criticità rilevata. La regola con la prima parola chiave corrispondente
            viene scelta in base all'ordine crescente. La regola contrassegnata come
            <em> default</em> viene usata quando nessuna parola chiave corrisponde.
          </p>
        </div>
        <div className="flex gap-2">
          <button className="btn btn-outline" onClick={resetDefaults} data-testid="reset-defaults-btn">
            <RotateCcw size={15} /> Ripristina default
          </button>
          <button className="btn btn-primary" onClick={startNew} data-testid="new-rule-btn">
            <Plus size={15} /> Nuova regola
          </button>
        </div>
      </div>

      {okMsg && (
        <div className="card" style={{borderColor:"#bbf7d0", background:"#f0fdf4", marginBottom:14}}>
          <div style={{padding:10, color:"#15803d", display:"flex", gap:8, alignItems:"center"}}>
            <Check size={16}/> {okMsg}
          </div>
        </div>
      )}

      {/* ---------- Test box ---------- */}
      <div className="card" style={{padding:16, marginBottom:18}} data-testid="test-box">
        <div className="flex items-center gap-2 mb-2" style={{fontWeight:700}}>
          <FlaskConical size={16}/> Banco di prova
        </div>
        <p style={{fontSize:13, color:"var(--mv-text-muted)", marginBottom:10}}>
          Incolla una frase di criticità (come compare nel PDF) per vedere quale
          regola viene applicata.
        </p>
        <div className="flex gap-2">
          <input
            type="text"
            value={testText}
            onChange={(e) => setTestText(e.target.value)}
            placeholder='Es. "Tempo di caricamento superiore a 6 secondi su mobile"'
            className="input"
            style={{flex:1}}
            data-testid="test-input"
            onKeyDown={(e) => e.key === "Enter" && runTest()}
          />
          <button className="btn btn-primary" onClick={runTest} data-testid="test-run-btn">Prova</button>
        </div>
        {testResult && (
          <div style={{marginTop:12, padding:12, background:"var(--mv-bg-soft)", borderRadius:8}} data-testid="test-result">
            <div style={{fontSize:12, fontWeight:700, marginBottom:4,
              color: testResult.matched ? "#15803d" : "#b45309"}}>
              {testResult.matched
                ? `MATCH su parola chiave: "${testResult.matched_keyword}" → ${testResult.rule?.category}`
                : `Nessun match → si applica il fallback "${testResult.rule?.category}"`}
            </div>
            <div style={{fontSize:13, color:"var(--mv-text)"}}>
              {testResult.rule?.explanation}
            </div>
          </div>
        )}
      </div>

      {/* ---------- Edit modal ---------- */}
      {editing && (
        <div className="card" style={{padding:18, marginBottom:18, borderColor:"var(--mv-primary)"}} data-testid="edit-form">
          <div className="flex justify-between items-center mb-3">
            <div style={{fontWeight:700, fontSize:15}}>
              {editing === "new" ? "Nuova regola" : `Modifica: ${editing.category}`}
            </div>
            <button className="btn-icon" onClick={cancelEdit} title="Annulla">
              <X size={16}/>
            </button>
          </div>

          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <label className="text-sm font-semibold mb-1 block">Categoria *</label>
              <input
                className="input"
                value={form.category}
                onChange={(e) => setForm({...form, category: e.target.value})}
                placeholder="Es. Velocità di caricamento"
                data-testid="form-category"
              />
            </div>
            <div>
              <label className="text-sm font-semibold mb-1 block">Ordine</label>
              <input
                type="number"
                className="input"
                value={form.order}
                onChange={(e) => setForm({...form, order: e.target.value})}
                placeholder="500"
                data-testid="form-order"
              />
            </div>
          </div>

          <div className="mb-3">
            <label className="text-sm font-semibold mb-1 block">
              Parole chiave (separate da virgola, in minuscolo)
            </label>
            <input
              className="input"
              value={form.keywords}
              onChange={(e) => setForm({...form, keywords: e.target.value})}
              placeholder="caricamento, loading, velocit, speed, lcp"
              disabled={form.is_default}
              data-testid="form-keywords"
            />
            <p style={{fontSize:11, color:"var(--mv-text-muted)", marginTop:4}}>
              La regola scatta se almeno una parola chiave è contenuta (anche
              parzialmente) nel testo della criticità. Le regole di default non
              usano keyword.
            </p>
          </div>

          <div className="mb-3">
            <label className="text-sm font-semibold mb-1 block">Spiegazione (min. 10 caratteri) *</label>
            <textarea
              className="input"
              rows={5}
              value={form.explanation}
              onChange={(e) => setForm({...form, explanation: e.target.value})}
              placeholder="Testo educativo che spiega l'impatto SEO / AI di questa criticità..."
              data-testid="form-explanation"
            />
            <p style={{fontSize:11, color:"var(--mv-text-muted)", marginTop:4}}>
              {form.explanation.length} caratteri.
            </p>
          </div>

          <div className="mb-3">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.is_default}
                onChange={(e) => setForm({...form, is_default: e.target.checked})}
                data-testid="form-default"
              />
              <Star size={14}/> Regola di default (fallback quando nessuna keyword corrisponde)
            </label>
          </div>

          {err && (
            <div style={{padding:10, background:"#fef2f2", color:"#b91c1c",
              borderRadius:6, marginBottom:10, fontSize:13,
              display:"flex", gap:8, alignItems:"center"}}>
              <AlertTriangle size={15}/> {err}
            </div>
          )}

          <div className="flex gap-2 justify-end">
            <button className="btn btn-outline" onClick={cancelEdit} disabled={saving}>Annulla</button>
            <button className="btn btn-primary" onClick={save} disabled={saving} data-testid="form-save-btn">
              <Save size={15}/> {saving ? "Salvataggio…" : "Salva"}
            </button>
          </div>
        </div>
      )}

      {/* ---------- Table ---------- */}
      {loading ? (
        <div><span className="spinner"/></div>
      ) : (
        <div className="card" data-testid="rules-table">
          <table style={{width:"100%", borderCollapse:"collapse"}}>
            <thead>
              <tr style={{borderBottom:"1px solid var(--mv-border)", textAlign:"left"}}>
                <th style={{padding:10, width:50, fontSize:12, color:"var(--mv-text-muted)"}}>Ord.</th>
                <th style={{padding:10, fontSize:12, color:"var(--mv-text-muted)"}}>Categoria</th>
                <th style={{padding:10, fontSize:12, color:"var(--mv-text-muted)"}}>Parole chiave</th>
                <th style={{padding:10, fontSize:12, color:"var(--mv-text-muted)"}}>Spiegazione</th>
                <th style={{padding:10, width:90, textAlign:"right", fontSize:12, color:"var(--mv-text-muted)"}}>Azioni</th>
              </tr>
            </thead>
            <tbody>
              {rules.map(r => (
                <tr key={r.id} style={{borderBottom:"1px solid var(--mv-border-light)"}} data-testid={`rule-row-${r.id}`}>
                  <td style={{padding:10, fontFamily:"monospace", color:"var(--mv-text-muted)", fontSize:12}}>
                    {r.order}
                  </td>
                  <td style={{padding:10, fontWeight:700}}>
                    {r.is_default && <Star size={12} style={{display:"inline", marginRight:4, color:"#b45309"}}/>}
                    {r.category}
                  </td>
                  <td style={{padding:10}}>
                    {r.is_default ? (
                      <span style={{fontSize:12, color:"var(--mv-text-muted)", fontStyle:"italic"}}>
                        — (fallback)
                      </span>
                    ) : (
                      <div style={{display:"flex", flexWrap:"wrap", gap:4}}>
                        {(r.keywords || []).map(k => (
                          <span key={k} style={{
                            background:"var(--mv-bg-soft)", padding:"2px 7px",
                            borderRadius:10, fontSize:11, fontFamily:"monospace"
                          }}>{k}</span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td style={{padding:10, fontSize:13, color:"var(--mv-text)", maxWidth:400}}>
                    <div style={{
                      overflow:"hidden", textOverflow:"ellipsis",
                      display:"-webkit-box", WebkitLineClamp:2, WebkitBoxOrient:"vertical"
                    }}>
                      {r.explanation}
                    </div>
                  </td>
                  <td style={{padding:10, textAlign:"right", whiteSpace:"nowrap"}}>
                    <button className="btn-icon" onClick={() => startEdit(r)}
                      title="Modifica" data-testid={`edit-btn-${r.id}`}>
                      <Pencil size={14}/>
                    </button>
                    <button className="btn-icon" onClick={() => remove(r)}
                      title={r.is_default ? "La regola di default non può essere eliminata" : "Elimina"}
                      disabled={r.is_default}
                      data-testid={`delete-btn-${r.id}`}>
                      <Trash2 size={14}/>
                    </button>
                  </td>
                </tr>
              ))}
              {!rules.length && (
                <tr><td colSpan={5} style={{padding:30, textAlign:"center", color:"var(--mv-text-muted)"}}>
                  Nessuna regola configurata.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
